import * as yup from 'yup';

import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Button, CardFormGroup, Input } from '../../../components';
import { Card, CardBody, CardFooter, CardSubtitle, Col, Form, FormGroup, Label, Row } from 'reactstrap';
import { ProgramResource, ProgramResourceDetail } from '../../../common/Types';
import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Skeleton from 'react-loading-skeleton';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';

interface ProgramResourceDetailForm extends ProgramResourceDetail {
    anyNo?: boolean;
}

const Resources = (props: AnnualReportProps) => {
    const [resources, setResources] = useState<ProgramResourceDetail>();
    const [reload, setReload] = useState<boolean>(true);
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

    const programResourceDetailSchema = yup
        .object({
            programResourceId: yup
                .number()
                .required()
                .default(resources?.programResourceId || 0),
            programId: yup.number().required().default(props.program.programId),
            activeYear: yup.number().required().default(+props.reportYear),
            anyNo: yup.boolean(),
            descriptionAnalysis: yup.string().notRequired().nullable(),
            actionPlan: yup.string().notRequired().nullable(),
            createdBy: yup.string().notRequired().nullable(),
            dateCreated: yup.date().notRequired().nullable().default(new Date()),
            lastEdited: yup.date().notRequired().nullable().default(new Date()),
            editedBy: yup.string().notRequired().nullable(),
            programResourceDetailDto: yup
                .array()
                .of(
                    yup
                        .object<ProgramResource>({
                            id: yup.number().required().default(0),
                            programResourceId: yup.number().required().default(0),
                            title: yup.string().required(),
                            requireDetail: yup.boolean().required(),
                            hasChanged: yup
                                .boolean()
                                .nullable()
                                .test(
                                    'hasChanged',
                                    'Please select whether or not the resource has changed.',
                                    function (value) {
                                        const { requireDetail } = (this as any).parent || {};
                                        const detailRequired = requireDetail === true || requireDetail?.toString() === 'true';
                                        if (detailRequired && (value === null || value === undefined)) {
                                            return (this as any).createError({
                                                message: 'Please select whether or not the resource has changed.',
                                            });
                                        }
                                        return true;
                                    },
                                )
                                .typeError('Please select Yes or No.')
                                .default(undefined),
                            status: yup
                                .boolean()
                                .typeError('Please select Yes or No.')
                                .required('Please select Yes or No.')
                                .defined(),
                            reason: yup
                                .string()
                                .test('reason', 'Please indicate what has changed.', function (value) {
                                    const { hasChanged } = (this as any).parent || {};
                                    const changed = hasChanged === true || hasChanged?.toString() === 'true';
                                    if (changed && (!value || value.trim() === '')) {
                                        return (this as any).createError({
                                            message: 'Please indicate what has changed.',
                                        });
                                    }
                                    return true;
                                })
                                .nullable(),
                        })
                        .defined(),
                )
                .min(1),
        })
        .defined();

    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );
    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<ProgramResourceDetailForm>({
        resolver: yupResolver(programResourceDetailSchema) as any,
        defaultValues: {} as ProgramResourceDetailForm,
    });
    const { fields } = useFieldArray({
        control,
        name: 'programResourceDetailDto',
    });

    const allFormValues = watch();
    const answers = allFormValues?.programResourceDetailDto as ProgramResource[];

    const [anyNo, setAnyNo] = useState(false);

    const recomputeAnyNo = useCallback(() => {
        const vals = getValues('programResourceDetailDto') as ProgramResource[];
        const hasNo = Array.isArray(vals) && vals.some((v) => v?.status?.toString() === 'false');
        setAnyNo(hasNo);
    }, [getValues]);

    // Subscribe to form changes to update anyNo reliably
    useEffect(() => {
        const subscription = watch((values) => {
            const resourceValues = values?.programResourceDetailDto as ProgramResource[];
            const hasNo =
                Array.isArray(resourceValues) &&
                resourceValues.some((v) => v?.status?.toString() === 'false');
            setAnyNo(hasNo);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        setValue('anyNo', anyNo);
    }, [anyNo, setValue]);

    const onSubmit = (formData: ProgramResourceDetailForm) => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving resources...');

        programService
            .saveProgramResources(props.program.programId, formData)
            .then((result) => {
                // Update form with server response (especially programResourceId for new records)
                if (result) {
                    reset(result);
                }
                toast.update(toastId, {
                    type: 'success',
                    render: 'Resources Saved.',
                });

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save resources.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const submitWithoutValidation = () => {
        setFormSubmitting(true);
        const formValues = getValues();

        const values = {
            programResourceId: formValues.programResourceId ?? resources?.programResourceId ?? 0,
            programId: props.program.programId,
            activeYear: +props.reportYear,
            programResourceDetailDto: formValues.programResourceDetailDto,
            actionPlan: formValues.actionPlan,
            descriptionAnalysis: formValues.descriptionAnalysis,
            createdBy: formValues.createdBy,
            dateCreated: formValues.dateCreated,
            lastEdited: formValues.lastEdited,
            editedBy: formValues.editedBy,
        } as unknown as ProgramResourceDetail;

        const toastId = toast.info('Saving resources...');

        programService
            .saveProgramResources(props.program.programId, values)
            .then((results) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Resources Saved.',
                });

                setReload(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save resources.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    useEffect(() => {
        const getResources = () => {
            if (reload) {
                setReload(false);
                programService
                    .getProgramResources(
                        props.program.programId,
                        props.profession.professionId,
                        props.reportYear ?? new Date().getFullYear(),
                    )
                    .then((response) => {
                        setResources(response);
                        reset(response);
                        // Initialize anyNo from loaded data
                        const hasNo =
                            response?.programResourceDetailDto?.some(
                                (v: any) => v?.status?.toString() === 'false',
                            ) ?? false;
                        setAnyNo(hasNo);
                    })
                    .catch(() => {
                        toast.error('Unable to get settings.');
                    });
            }
        };

        getResources();
    }, [props.program.programId, props.reportYear, setResources, props.profession.professionId, reset, reload]);

    const showReason = (answers, resource): boolean => {
        if (!answers && resource.reason) {
            return true;
        }

        if (answers && !answers.find((a) => a.title === resource.title) && resource.reason) {
            return true;
        }

        if (
            answers &&
            answers.find(
                (a) => a.title === resource.title && (a.hasChanged?.toString() === 'true' || a.hasChanged === true),
            ) !== undefined
        ) {
            return true;
        }

        if (
            answers &&
            answers.find(
                (a) => a.title === resource.title && (a.hasChanged?.toString() === 'true' || a.hasChanged === true),
            ) === undefined &&
            resource.reason
        ) {
            return true;
        }

        return false;
    };

    return (
        <>
            <Col>
                <Card>
                    <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                        const fieldNames = Object.keys(validationErrors);
                        toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
                    })}>
                        <Controller
                            name="anyNo"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value?.toString() ?? ''} />
                            )}
                        />
                        <Controller
                            name="programResourceId"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? resources?.programResourceId ?? 0} />
                            )}
                        />
                        <Controller
                            name="programId"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? props.program.programId} />
                            )}
                        />
                        <Controller
                            name="activeYear"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? +props.reportYear} />
                            )}
                        />
                        {(resources?.programResourceId || 0) > 0 && (
                            <>
                                <Controller
                                    name="createdBy"
                                    control={control}
                                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                                />
                                <Controller
                                    name="dateCreated"
                                    control={control}
                                    render={({ field }) => (
                                        <input type="hidden" {...field} value={field.value?.toString() ?? ''} />
                                    )}
                                />
                                <Controller
                                    name="lastEdited"
                                    control={control}
                                    render={({ field }) => (
                                        <input type="hidden" {...field} value={field.value?.toString() ?? ''} />
                                    )}
                                />
                                <Controller
                                    name="editedBy"
                                    control={control}
                                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                                />
                            </>
                        )}
                        <CardBody>
                            <h3 className="card-title">Resources</h3>
                            <CardSubtitle className="mb-3">
                                {`Are the following resources sufficient to ensure the achievement of a program's
                                goals and outcomes?`}
                            </CardSubtitle>
                            {resources ? (
                                <>
                                    <FormGroup>
                                        {fields.map((resource, i) => (
                                            <CardFormGroup key={i}>
                                                <Controller
                                                    name={`programResourceDetailDto[${i}].id` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? resource.id ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`programResourceDetailDto[${i}].programResourceId` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? resource.programResourceId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`programResourceDetailDto[${i}].title` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? resource.title ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`programResourceDetailDto[${i}].requireDetail` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={
                                                                field.value ?? resource.requireDetail?.toString() ?? ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <Label
                                                    className="standard-label"
                                                    for={`programResourceDetailDto[${i}].status`}
                                                >
                                                    {resource.title}
                                                </Label>
                                                <Row>
                                                    <Col md={6}>
                                                        <Controller
                                                            name={`programResourceDetailDto[${i}].status` as any}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <div className="radio-group">
                                                                    <div className="custom-control custom-radio">
                                                                        <input
                                                                            type="radio"
                                                                            id={`programResourceDetailDto[${i}].statusYes`}
                                                                            className="custom-control-input"
                                                                            name={field.name}
                                                                            checked={field.value === 'true' || field.value === true}
                                                                            value="true"
                                                                            onChange={() => {
                                                                                field.onChange('true');
                                                                                setTimeout(recomputeAnyNo, 0);
                                                                            }}
                                                                        />
                                                                        <label className="custom-control-label" htmlFor={`programResourceDetailDto[${i}].statusYes`}>
                                                                            Yes
                                                                        </label>
                                                                    </div>
                                                                    <div className="custom-control custom-radio">
                                                                        <input
                                                                            type="radio"
                                                                            id={`programResourceDetailDto[${i}].statusNo`}
                                                                            className="custom-control-input"
                                                                            name={field.name}
                                                                            checked={field.value === 'false' || field.value === false}
                                                                            value="false"
                                                                            onChange={() => {
                                                                                field.onChange('false');
                                                                                setTimeout(recomputeAnyNo, 0);
                                                                            }}
                                                                        />
                                                                        <label className="custom-control-label" htmlFor={`programResourceDetailDto[${i}].statusNo`}>
                                                                            No
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        />
                                                        {errors.programResourceDetailDto &&
                                                            errors.programResourceDetailDto[i] && (
                                                                <Label className={'text-danger'}>
                                                                    {
                                                                        errors.programResourceDetailDto[i]?.status
                                                                            ?.message
                                                                    }
                                                                </Label>
                                                            )}
                                                    </Col>
                                                </Row>
                                                {resource.requireDetail?.toString() === 'true' && (
                                                    <>
                                                        <Label
                                                            className="standard-label"
                                                            for={`programResourceDetailDto[${i}].hasChanged`}
                                                        >
                                                            {`Have these resources changed over the past year?`}
                                                        </Label>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Controller
                                                                    name={`programResourceDetailDto[${i}].hasChanged` as any}
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <div className="radio-group">
                                                                            <div className="custom-control custom-radio">
                                                                                <input
                                                                                    type="radio"
                                                                                    id={`programResourceDetailDto[${i}].hasChangedYes`}
                                                                                    className="custom-control-input"
                                                                                    name={field.name}
                                                                                    checked={field.value === 'true' || field.value === true}
                                                                                    value="true"
                                                                                    onChange={() => field.onChange('true')}
                                                                                />
                                                                                <label className="custom-control-label" htmlFor={`programResourceDetailDto[${i}].hasChangedYes`}>
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className="custom-control custom-radio">
                                                                                <input
                                                                                    type="radio"
                                                                                    id={`programResourceDetailDto[${i}].hasChangedNo`}
                                                                                    className="custom-control-input"
                                                                                    name={field.name}
                                                                                    checked={field.value === 'false' || field.value === false}
                                                                                    value="false"
                                                                                    onChange={() => field.onChange('false')}
                                                                                />
                                                                                <label className="custom-control-label" htmlFor={`programResourceDetailDto[${i}].hasChangedNo`}>
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                />
                                                                {errors.programResourceDetailDto &&
                                                                    errors.programResourceDetailDto[i] && (
                                                                        <Label className={'text-danger'}>
                                                                            {
                                                                                errors.programResourceDetailDto[i]
                                                                                    ?.hasChanged?.message
                                                                            }
                                                                        </Label>
                                                                    )}
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                                {showReason(answers, resource) && (
                                                    <FormGroup>
                                                        <Label for={`programResourceDetailDto[${i}].reason`}>
                                                            {`Describe changes to this resource`}
                                                        </Label>
                                                        <Controller
                                                            name={`programResourceDetailDto[${i}].reason` as any}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type="textarea"
                                                                    {...field}
                                                                    value={field.value ?? resource.reason ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        {errors.programResourceDetailDto &&
                                                            errors.programResourceDetailDto[i] && (
                                                                <Label className={'text-danger'}>
                                                                    {
                                                                        errors.programResourceDetailDto[i]?.reason
                                                                            ?.message
                                                                    }
                                                                </Label>
                                                            )}
                                                    </FormGroup>
                                                )}
                                            </CardFormGroup>
                                        ))}
                                    </FormGroup>
                                    {anyNo === true && (
                                        <FormGroup>
                                            <Row>
                                                <Col className={'d-flex flex-column'}>
                                                    <Label>{`Description/Analysis of each insufficient resource`}</Label>
                                                    <Controller
                                                        name="descriptionAnalysis"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="textarea"
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                rows={10}
                                                            />
                                                        )}
                                                    />
                                                    {errors && errors.descriptionAnalysis && (
                                                        <Label className={'text-danger'}>
                                                            {errors.descriptionAnalysis?.message}
                                                        </Label>
                                                    )}
                                                </Col>
                                                <Col className={'d-flex flex-column'}>
                                                    <Label>{`Action Plan for each insufficient resource`}</Label>
                                                    <Controller
                                                        name="actionPlan"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="textarea"
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                rows={10}
                                                            />
                                                        )}
                                                    />
                                                    {errors && errors.actionPlan && (
                                                        <Label className={'text-danger'}>
                                                            {errors.actionPlan?.message}
                                                        </Label>
                                                    )}
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Card>
                                        <CardBody>
                                            <Skeleton count={2} />
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Skeleton count={2} />
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Skeleton count={2} />
                                        </CardBody>
                                    </Card>
                                </>
                            )}
                        </CardBody>
                        {props.canEdit && (
                            <CardFooter className={'d-flex justify-content-between'}>
                                <Button
                                    type={'button'}
                                    color={'warning'}
                                    onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                                >
                                    <i className={'mdi mdi-arrow-left'} />
                                    <span className={'mr-1'}>{`Previous`}</span>
                                </Button>

                                <Button
                                    disabled={formSubmitting}
                                    type={'button'}
                                    color={'primary'}
                                    onClick={() => submitWithoutValidation()}
                                >
                                    <i className={'mdi mdi-content-save'} />
                                    <span className={'ml-1'}>{`Save`}</span>
                                </Button>

                                <Button
                                    disabled={formSubmitting}
                                    type={'submit'}
                                    color={'info'}
                                    onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                                >
                                    <span className={'mr-1'}>{`Save & Continue`}</span>
                                    <i className={'mdi mdi-arrow-right'} />
                                </Button>
                            </CardFooter>
                        )}
                    </Form>
                </Card>
            </Col>
        </>
    );
};

export default Resources;
